import { useEffect, useState } from 'react'
import { getAdDeviceType, isDesktop } from '../../client'
import { useGoogleAds } from './useGoogleAds'

type Props = {
  id?: AdId
  mobileAdId?: AdId
  desktopAdId?: AdId
  renderCallback?: (event: googletag.events.SlotRenderEndedEvent) => void
}

export type MobileAdId = keyof MobileAdUnitSlots
export type DesktopAdId = keyof DesktopAdUnitSlots
export type AppAdId = keyof AppAdUnitSlots

type BaseAdId = MobileAdId | DesktopAdId | AppAdId

export type AdId = BaseAdId | `${BaseAdId}${string}`

type AdDeviceType = 'desktop' | 'mobile' | 'app'

// Singleton cache: one loaded config object per device type, shared across all hook instances
const configCache: Partial<
  Record<AdDeviceType, DesktopAdUnitSlots | MobileAdUnitSlots | AppAdUnitSlots>
> = {}
// In-flight promise cache: prevents duplicate simultaneous imports for the same device type
const configPromiseCache: Partial<
  Record<
    AdDeviceType,
    Promise<DesktopAdUnitSlots | MobileAdUnitSlots | AppAdUnitSlots>
  >
> = {}

const loadConfigData = (
  adDeviceType: AdDeviceType
): Promise<DesktopAdUnitSlots | MobileAdUnitSlots | AppAdUnitSlots> => {
  const cached = configCache[adDeviceType]
  if (cached) {
    return Promise.resolve(cached)
  }

  if (!configPromiseCache[adDeviceType]) {
    configPromiseCache[adDeviceType] = (async () => {
      let data: DesktopAdUnitSlots | MobileAdUnitSlots | AppAdUnitSlots

      if (adDeviceType === 'desktop') {
        const module = await import(
          '../../../public/static/ad_units_slot_desktop.json'
        )
        data = module.default as unknown as DesktopAdUnitSlots
      } else if (adDeviceType === 'mobile') {
        const module = await import(
          '../../../public/static/ad_units_slot_mobile.json'
        )
        data = module.default as unknown as MobileAdUnitSlots
      } else {
        const module = await import(
          '../../../public/static/ad_units_slot_app.json'
        )
        data = module.default as unknown as AppAdUnitSlots
      }

      configCache[adDeviceType] = data
      return data
    })()
  }

  const pending = configPromiseCache[adDeviceType]
  if (!pending) {
    throw new Error(`No config promise found for device type: ${adDeviceType}`)
  }
  return pending
}

export const useAdConfigs = ({
  id,
  mobileAdId,
  desktopAdId,
  renderCallback,
}: Props): IAdConfig | null => {
  const [adConfig, setAdConfig] = useState<IAdConfig | null>(null)
  const { renderCallbacks } = useGoogleAds()

  useEffect(() => {
    const finalAdId = id || (isDesktop() ? desktopAdId : mobileAdId)
    if (!finalAdId) {
      console.warn('no ad id defined', { id, desktopAdId, mobileAdId })
      return
    }
    if (renderCallback) {
      renderCallbacks.push({ id: finalAdId, callback: renderCallback })
    }

    const adDeviceType = getAdDeviceType()

    const loadConfig = async () => {
      try {
        const configData = await loadConfigData(adDeviceType)
        const config = configData[
          finalAdId as keyof typeof configData
        ] as IAdConfig
        if (config) {
          setAdConfig({ ...config, domId: finalAdId })
        }
      } catch (error) {
        console.error('Failed to load ad config dynamically', error)
      }
    }

    loadConfig()

    return () => {
      const index = renderCallbacks.findIndex(cb => cb.id === finalAdId)
      renderCallbacks.splice(index, 1)
    }
  }, [desktopAdId, id, mobileAdId, renderCallback, renderCallbacks])

  return adConfig
}

type DesktopAdUnitSlots = {
  Top: IAdConfig
  Inboard: IAdConfig
  Poster: IAdConfig
  Box_7_1: IAdConfig
  Box_7_2: IAdConfig
  Box_7_3: IAdConfig
  Box_3_1: IAdConfig
  Box_3_2: IAdConfig
  Box_3_3: IAdConfig
  Strip1: IAdConfig
  Strip2: IAdConfig
  Strip3: IAdConfig
  FooterStrip: IAdConfig
  In_article_box_1: IAdConfig
  In_article_box_2: IAdConfig
  In_article_box_3: IAdConfig
  In_article_box_4: IAdConfig
  Maavaron_Desktop: IAdConfig
  Maavaron_Desktop_T: IAdConfig
  Inread: IAdConfig
  Ozen_right: IAdConfig
  Ozen_left: IAdConfig
  VodPlaylist_box_1: IAdConfig
  VodPlaylist_box_2: IAdConfig
  VodPlaylist_box_3: IAdConfig
  UpdateBox: IAdConfig
}

type MobileAdUnitSlots = {
  Maavaron_main: IAdConfig
  Maavaron_ros: IAdConfig
  Maavaron_ros_T: IAdConfig
  Top_mobile: IAdConfig
  Mid_box_1: IAdConfig
  Mid_box_2: IAdConfig
  Mid_box_3: IAdConfig
  Mid_box_4: IAdConfig
  Footerstrip: IAdConfig
  In_article_box_1: IAdConfig
  In_article_box_2: IAdConfig
  In_article_box_3: IAdConfig
  In_article_box_4: IAdConfig
  In_article_box_5: IAdConfig
  VodPlaylist_box_1: IAdConfig
  VodPlaylist_box_2: IAdConfig
  VodPlaylist_box_3: IAdConfig
  UpdateBox: IAdConfig
}

type AppAdUnitSlots = {
  Maavaron_main: IAdConfig
  Top_mobile: IAdConfig
  Mid_box_1: IAdConfig
  Mid_box_2: IAdConfig
  Mid_box_3: IAdConfig
  Mid_box_4: IAdConfig
  In_article_box_1: IAdConfig
  In_article_box_2: IAdConfig
  In_article_box_3: IAdConfig
  In_article_box_4: IAdConfig
  In_article_box_5: IAdConfig
  VodPlaylist_box_1: IAdConfig
  VodPlaylist_box_2: IAdConfig
  VodPlaylist_box_3: IAdConfig
  Main_Footer_web: IAdConfig
  Ros_Footer_web: IAdConfig
  UpdateBox: IAdConfig
}

export type IAdConfig = {
  id: string
  name: string
  size: number[][] | number[] | string[]
  domId: string
}
